import { useMemo, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import { LogEntry, GroupedClientSummary } from '../../interface/logEntry';
import {
  StyledTableCell as BaseStyledTableCell,
  StyledTableCellFooter as BaseStyledTableCellFooter,
  StyledTableRow
} from './shared/StyledTableComponents';

interface ClientSummaryTableProps {
  logs: LogEntry[];
}

/**
 * Extended StyledTableCell with ClientSummaryTable-specific alignment
 * - First column (Client Name): left-aligned
 * - Second column (Total Hours): right-aligned and bold
 */
const StyledTableCell = styled(BaseStyledTableCell)({
  textAlign: 'left',
  '&:last-child': {
    textAlign: 'right',
    fontWeight: 'bold',
  },
});

/**
 * Extended StyledTableCellFooter with alignment for footer row
 * - First column (GRAND TOTAL text): left-aligned
 * - Second column (total value): right-aligned
 */
const StyledTableCellFooter = styled(BaseStyledTableCellFooter)({
  textAlign: 'left',
  '&:last-child': {
    textAlign: 'right',
  },
});

const PeachTotalCell = styled(BaseStyledTableCellFooter)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    backgroundColor: theme.palette.table.totalHighlight,
    color: theme.palette.text.primary,
    textAlign: 'right',
    fontWeight: 'bold',
  },
}));

/**
 * Extract the base client name from a full client name.
 * "Euro Solutions - ClaimUW" -> "Euro Solutions"
 * "Acme Corp" -> "Acme Corp"
 */
const getBaseClientName = (clientName: string): string => {
  const separatorIndex = clientName.indexOf(' - ');
  return separatorIndex !== -1 ? clientName.substring(0, separatorIndex) : clientName;
};

/**
 * Extract the project name from a full client name.
 * "Euro Solutions - ClaimUW" -> "ClaimUW"
 * "Acme Corp" -> null
 */
const getProjectName = (clientName: string): string | null => {
  const separatorIndex = clientName.indexOf(' - ');
  return separatorIndex !== -1 ? clientName.substring(separatorIndex + 3) : null;
};

const ClientSummaryTable = ({ logs }: ClientSummaryTableProps) => {
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());

  // Calculate grouped client summaries from logs (memoized)
  const groupedSummaries = useMemo<GroupedClientSummary[]>(() => {
    // First, aggregate by full client name
    const fullNameMap = new Map<string, number>();
    logs.forEach(log => {
      if (log.duration !== null && log.client_name) {
        const currentMinutes = fullNameMap.get(log.client_name) || 0;
        fullNameMap.set(log.client_name, currentMinutes + log.duration);
      }
    });

    // Group by base client name
    const groupMap = new Map<string, GroupedClientSummary>();
    fullNameMap.forEach((totalMinutes, fullName) => {
      const baseName = getBaseClientName(fullName);
      const projectName = getProjectName(fullName);
      const hours = totalMinutes / 60;

      if (!groupMap.has(baseName)) {
        groupMap.set(baseName, {
          clientName: baseName,
          totalHours: 0,
          projects: [],
        });
      }

      const group = groupMap.get(baseName)!;
      group.totalHours += hours;

      if (projectName) {
        group.projects.push({
          clientName: projectName,
          totalHours: hours,
        });
      }
    });

    // Sort groups alphabetically, and sort projects within each group
    const result = Array.from(groupMap.values()).sort((a, b) =>
      a.clientName.localeCompare(b.clientName)
    );
    result.forEach(group => {
      group.projects.sort((a, b) => a.clientName.localeCompare(b.clientName));
    });

    return result;
  }, [logs]);

  // Calculate grand total
  const grandTotal = useMemo(() => {
    return groupedSummaries.reduce((sum, group) => sum + group.totalHours, 0);
  }, [groupedSummaries]);

  const toggleExpand = (clientName: string) => {
    setExpandedClients(prev => {
      const next = new Set(prev);
      if (next.has(clientName)) {
        next.delete(clientName);
      } else {
        next.add(clientName);
      }
      return next;
    });
  };

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 700 }} aria-label="client summary table">
        <TableHead>
          <TableRow>
            <StyledTableCell>Client</StyledTableCell>
            <StyledTableCell>Total Hours</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groupedSummaries.map((group) => {
            const hasProjects = group.projects.length > 1;
            const isExpanded = expandedClients.has(group.clientName);

            return (
              <>
                <StyledTableRow key={group.clientName}>
                  <StyledTableCell>
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      {hasProjects && (
                        <IconButton
                          size="small"
                          onClick={() => toggleExpand(group.clientName)}
                          sx={{ mr: 0.5, p: 0 }}
                        >
                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      )}
                      <span style={{ fontWeight: hasProjects ? 600 : 'normal' }}>
                        {group.clientName}
                      </span>
                    </span>
                  </StyledTableCell>
                  <StyledTableCell>{group.totalHours.toFixed(2)}h</StyledTableCell>
                </StyledTableRow>
                {hasProjects && (
                  <TableRow key={`${group.clientName}-projects`}>
                    <td colSpan={2} style={{ padding: 0, border: 0 }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Table size="small">
                          <TableBody>
                            {group.projects.map((project) => (
                              <StyledTableRow key={project.clientName}>
                                <StyledTableCell sx={{ pl: 5 }}>
                                  {project.clientName}
                                </StyledTableCell>
                                <StyledTableCell>{project.totalHours.toFixed(2)}h</StyledTableCell>
                              </StyledTableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Collapse>
                    </td>
                  </TableRow>
                )}
              </>
            );
          })}
          <StyledTableRow>
            <StyledTableCellFooter>GRAND TOTAL</StyledTableCellFooter>
            <PeachTotalCell>{grandTotal.toFixed(2)}h</PeachTotalCell>
          </StyledTableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ClientSummaryTable;